import { flatten, compact, get, getOr, cloneDeep, uniqBy, has } from 'lodash/fp'

import { sendLinkCCPA, unsetAppValue } from 'e-submission/apps/actions'
import {
  getDefaultPersonalTitleList,
  getKidPersonalTitleList,
  getRelationshipListForBeneficiary,
} from 'e-submission/apps/selectors'
import { DOCUMENT_TYPE } from 'e-submission/domain/data-model/constants'
import { isCCPASendLink, isCCPAStatusCompleted } from 'e-submission/domain/data-model/form/selectors'
import { createField, createSectionCreator } from 'e-submission/domain/data-model/form/utils'
import { validateEmail } from 'e-submission/utils'

const CCPA = 'CCPA'
const CCPAStatus = 'CCPAStatus'

function hasPayer(app) {
  return !!get('payer.firstName')(app)
}

function checkPayerIsExist(app) {
  const payerFirstName = get('payer.firstName')(app)
  const payerRelationshipToInsured = get('payer.relationshipToInsured.value')(app)
  return !payerFirstName || (payerRelationshipToInsured !== 'SPOUSE' && payerRelationshipToInsured !== 'PARENT')
}

function checkInformationIsFilled(state) {
  const validationProperties = ['relations', 'title', 'firstName', 'lastName']
  for (let property of validationProperties) {
    if (!getCurrentAppIdAndStateData(state, property).stateTargetData) return false
  }
  const citizenId = getCurrentAppIdAndStateData(state, 'idNo.citizenId').stateTargetData
  const passport = getCurrentAppIdAndStateData(state, 'idNo.passport').stateTargetData
  const idType = getCurrentAppIdAndStateData(state, 'idNo.idType.value').stateTargetData
  if (idType === 'PASSPORT') {
    if (!passport || citizenId) return false
  } else {
    if (!citizenId || citizenId.length !== 13 || passport) return false
  }
  const sendLinkMethod = getCurrentAppIdAndStateData(state, 'sendLink.method.value').stateTargetData
  if (sendLinkMethod === 'sms') {
    const mobileNumber = getCurrentAppIdAndStateData(state, `sendLink.${sendLinkMethod}`).stateTargetData
    return mobileNumber && mobileNumber.length === 10
  }

  return validateEmail(getCurrentAppIdAndStateData(state, `sendLink.${sendLinkMethod}`).stateTargetData)
}

function getDefaultFromPayerOrInsured(app, field) {
  const payerData = get(`payer.${field}`)(app)
  const insuredData = get(`insured.${field}`)(app)
  const payerFirstname = get('payer.firstName')(app)
  const payerRelationshipToInsured = get('payer.relationshipToInsured.value')(app)

  if (payerFirstname) {
    if (
      (payerRelationshipToInsured === 'PARENT' || payerRelationshipToInsured === 'SPOUSE') &&
      (field === 'mobileNumber' || field === 'email')
    ) {
      return ''
    }
    if (payerRelationshipToInsured === 'PARENT' || payerRelationshipToInsured === 'SPOUSE') {
      return payerData || insuredData
    }
  }

  return insuredData
}

function getCurrentAppIdAndStateData(state, field) {
  const currentAppId = get('apps.currentAppId')(state)
  const stateTargetData = state ? get(`apps.byId[${currentAppId}].${CCPA}.${field}`)(state) : null

  return {
    currentAppId,
    stateTargetData,
  }
}

export function buildCCPADefaultValue(app) {
  const hasPayerData = hasPayer(app)
  const passport = hasPayerData ? get('payer.idNo.passport')(app) : get('insured.idNo.passport')(app)
  const citizenId = hasPayerData ? get('payer.idNo.citizenId')(app) : get('insured.idNo.citizenId')(app)
  const defaultValue = {
    relations: checkPayerIsExist(app)
      ? { text: 'ตัวเอง', value: 'SELF' }
      : getDefaultFromPayerOrInsured(app, 'relationshipToInsured'),
    title: getDefaultFromPayerOrInsured(app, 'title'),
    firstName: getDefaultFromPayerOrInsured(app, 'firstName'),
    lastName: getDefaultFromPayerOrInsured(app, 'lastName'),
    sendLink: {
      sms: getDefaultFromPayerOrInsured(app, 'mobileNumber'),
      email: getDefaultFromPayerOrInsured(app, 'email'),
      method: {
        value: getOr('sms', `${CCPA}.sendLink.method.value`, app),
      },
    },
    idNo: { citizenId, passport },
  }

  defaultValue.idNo.idType =
    passport && !citizenId
      ? { value: 'PASSPORT', text: 'หนังสือเดินทาง' }
      : { value: 'CITIZEN_ID', text: 'บัตรประจำตัวประชาชน' }

  return defaultValue
}

function checkDefaultValue(app, field, state) {
  const { currentAppId, stateTargetData } = getCurrentAppIdAndStateData(state, field)
  const stateData = state ? get(`apps.byId[${currentAppId}].${CCPA}`)(state) : null
  if (stateTargetData || (stateData && has(field)(stateData)) || typeof stateTargetData === 'string') {
    return stateTargetData
  }

  const ccpaFieldTargetData = get(`${CCPA}.${field}`)(app)
  const ccpaData = get(`${CCPA}`)(app)

  if (ccpaFieldTargetData || (ccpaData && has(field)(ccpaData))) {
    return ccpaFieldTargetData
  }

  const defaultValue = buildCCPADefaultValue(app)
  return get(field)(defaultValue)
}

export const CCPAFormSection = (app) => {
  const section = { id: CCPA }
  const id = section.id
  const methodSelected = get(`${CCPA}.${'sendLink.method.value'}`)(app) || 'sms'
  const componentForTextInputConnecting = methodSelected === 'sms' ? 'NumberInput' : 'TextInput'
  return compact([
    createField(`${id}.title`, [
      {
        label: 'คำนำหน้า',
        id: `${id}.title`,
        c: 'Dropdown',
        required: true,
        p: {
          placeHolder: 'กรุณาเลือกคำนำหน้าชื่อ',
          mergeState: (state, app) => ({
            options: uniqBy('value')([...getDefaultPersonalTitleList(state), ...getKidPersonalTitleList(state)]),
            value: checkDefaultValue(app, 'title', state),
          }),
        },
      },
    ]),
    createField(`${id}.firstName`, [
      {
        label: 'ชื่อเจ้าของบัตรเครดิต',
        id: `${id}.firstName`,
        required: true,
        c: 'TextInput',
        p: {
          mergeState: (state, app) => {
            return {
              value: checkDefaultValue(app, 'firstName', state),
            }
          },
        },
      },
    ]),
    createField(`${id}.lastName`, [
      {
        label: 'นามสกุลเจ้าของบัตรเครดิต',
        id: `${id}.lastName`,
        required: true,
        c: 'TextInput',
        p: {
          mergeState: (state, app) => {
            return {
              value: checkDefaultValue(app, 'lastName', state),
            }
          },
        },
      },
    ]),
    createField(`${id}.idNo.idType`, [
      {
        label: 'เอกสารที่ใช้แสดง',
        id: `${id}.idNo.idType`,
        required: true,
        c: 'Dropdown',
        p: {
          mergeState: (state, app, { dispatch }) => {
            const idTypeValue = checkDefaultValue(app, 'idNo.idType', state)
            return {
              options: [
                {
                  value: 'CITIZEN_ID',
                  text: 'บัตรประจำตัวประชาชน',
                },
                {
                  value: 'PASSPORT',
                  text: 'หนังสือเดินทาง',
                },
              ],
              value: idTypeValue,
              customOnChange: (option) => {
                if (option?.value === 'PASSPORT') {
                  dispatch(unsetAppValue(`${CCPA}.idNo.citizenId`))
                } else if (option?.value === 'CITIZEN_ID') {
                  dispatch(unsetAppValue(`${CCPA}.idNo.passport`))
                }
              },
            }
          },
        },
      },
    ]),
    get('CCPA.idNo.idType.value')(app) !== 'PASSPORT' &&
      createField(`${id}.idNo.citizenId`, [
        {
          label: 'เลขประจำตัวประชาชน',
          id: `${id}.idNo.citizenId`,
          c: 'NumberInput',
          required: true,
          p: {
            mergeState: (state, app) => {
              return {
                value: checkDefaultValue(app, 'idNo.citizenId', state),
              }
            },
          },
        },
      ]),
    get('CCPA.idNo.idType.value')(app) === 'PASSPORT' &&
      createField(`${id}.idNo.passport`, [
        {
          label: 'หมายเลขหนังสือเดินทาง',
          id: `${id}.idNo.passport`,
          c: 'TextInput',
          required: true,
          p: {
            mergeState: (state, app) => {
              return {
                value: checkDefaultValue(app, 'idNo.passport', state),
              }
            },
          },
        },
      ]),
    createField(`${id}.relations`, [
      {
        label: 'ความสัมพันธ์กับผู้เอาประกัน',
        id: `${id}.relations`,
        required: true,
        c: 'Dropdown',
        p: {
          mergeState: (state) => {
            const relationshipListForBeneficiary = cloneDeep(getRelationshipListForBeneficiary(state))
            if (checkPayerIsExist(app)) relationshipListForBeneficiary.push({ text: 'ตัวเอง', value: 'SELF' })
            return {
              options: [...relationshipListForBeneficiary],
              value: checkDefaultValue(app, 'relations', state),
            }
          },
          placeHolder: 'กรุณาเลือกความสัมพันธ์',
        },
      },
    ]),
    createField(`${id}.sendLink.method`, [
      {
        label: 'ท่านต้องการส่งลิงก์สมัครหักค่าเบี้ยประกันอัตโนมัติผ่านบัตรเครดิตไปที่',
        id: `${id}.sendLink.method`,
        c: 'RadioButton',
        required: true,
        p: {
          options: [
            {
              text: 'อีเมล',
              value: 'email',
            },
            {
              text: 'SMS',
              value: 'sms',
            },
          ],
          mergeState: (state, app) => ({
            value: checkDefaultValue(app, 'sendLink.method', state),
          }),
        },
      },
    ]),
    createField(`${id}.sendLink.textInput`, [
      {
        id: `${id}.sendLink.${methodSelected}`,
        c: componentForTextInputConnecting,
        required: true,
        p: {
          mergeState: (state) => {
            return {}
          },
        },
        infoMessage: 'ท่านสามารถเลือกช่องทางในการส่งลิงก์ผ่านช่องทางที่ท่านต้องการ',
      },
    ]),
    createField(`${id}.submit`, [
      {
        id: `${id}.submit`,
        c: 'ButtonWithLoading',
        p: {
          style: {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          },
          mergeState: (state, app, { dispatch, history }) => {
            const isSendingLink = get(`${CCPA}.isSendingLink`)(app) === true
            const isInformationFilled = checkInformationIsFilled(state)
            return {
              loading: isSendingLink,
              disabled: isSendingLink || !isInformationFilled,
              onClick: () => {
                if (isSendingLink || !isInformationFilled) return
                dispatch(sendLinkCCPA())
              },
            }
          },
          children: 'ยืนยัน',
          block: true,
        },
      },
    ]),
  ])
}

const StatusSection = (app) => {
  return [
    createField(
      `${CCPAStatus}`,
      compact([
        {
          id: 'document',
          c: 'CcpaStatusCard',
          p: {},
        },
        {
          id: 'CCPA.preview',
          c: 'UnsignedDocument',
          p: {
            mergeState: (state, app) => {
              return {
                disabled: !isCCPAStatusCompleted(app),
              }
            },
            description: DOCUMENT_TYPE.insured.CCPA.text,
            name: DOCUMENT_TYPE.insured.CCPA.name,
            policyId: app.policyId,
            hasBackground: true,
          },
        },
      ])
    ),
  ]
}

export const CCPASection = createSectionCreator((app, id, section) => {
  const CCPAFormFields = CCPAFormSection(app)
  const StatusFields = StatusSection(app)
  return flatten(compact([isCCPASendLink(app) ? [...StatusFields] : [...CCPAFormFields]]))
})
